import React, { useState } from 'react';
import { ArrowLeft, Eye, Save, Loader2 } from 'lucide-react';
import ProgressSidebar from './ProgressSidebar';

/**
 * Enhanced Template Builder Wrapper
 * This wraps your existing template configuration form with better UX
 * while preserving ALL existing functionality
 */
const EnhancedTemplateBuilder = ({
    selectedTemplate,
    formData,
    currentSection,
    sections,
    completedSections,
    validationErrors,
    isSubmitting,
    onBack,
    onSectionChange,
    onPreview,
    onSubmit,
    children, // This is your existing form content - passed as children
}) => {
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    // Calculate progress
    const completedCount = sections.filter(s => completedSections.has(s.id)).length;
    const totalCount = sections.length;
    const progressPercentage = (completedCount / totalCount) * 100;

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            {/* Top Navigation Bar */}
            <div className="sticky top-0 z-50 bg-white border-b border-amber-200 shadow-lg backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex items-center justify-between h-20">
                        {/* Left: Back Button & Template Info */}
                        <div className="flex items-center gap-6">
                            <button
                                onClick={onBack}
                                className="flex items-center gap-3 px-6 py-3 text-amber-700 hover:bg-amber-50 rounded-xl transition-all font-semibold border border-amber-200 hover:border-amber-300 shadow-sm"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                <span className="hidden sm:inline">Back to Templates</span>
                                <span className="sm:hidden">Back</span>
                            </button>
                            <div className="hidden md:block">
                                <h2 className="text-lg font-bold text-gray-900">
                                    {selectedTemplate?.name} Template Configuration
                                </h2>
                                <p className="text-sm text-amber-700 font-medium">
                                    {completedCount} of {totalCount} sections completed
                                </p>
                            </div>
                        </div>

                        {/* Center: Progress Bar (Mobile) */}
                        <div className="md:hidden flex-1 mx-6">
                            <div className="h-3 bg-amber-100 rounded-full overflow-hidden border border-amber-200">
                                <div
                                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-700"
                                    style={{ width: `${progressPercentage}%` }}
                                />
                            </div>
                        </div>

                        {/* Right: Action Buttons */}
                        <div className="flex items-center gap-4">
                            <button
                                onClick={onPreview}
                                className="hidden sm:flex items-center gap-2 px-6 py-3 border-2 border-amber-300 text-amber-700 hover:border-amber-500 hover:bg-amber-50 rounded-xl transition-all font-semibold shadow-sm hover:shadow-md"
                            >
                                <Eye className="w-5 h-5" />
                                <span>Preview Template</span>
                            </button>
                            <button
                                onClick={onSubmit}
                                disabled={isSubmitting}
                                className="flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all font-bold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Saving Configuration...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        <span className="hidden sm:inline">Save & Publish Template</span>
                                        <span className="sm:hidden">Save</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Sidebar: Progress & Navigation */}
                    <div className="lg:col-span-1">
                        <div className="hidden lg:block">
                            <ProgressSidebar
                                sections={sections}
                                currentSection={currentSection}
                                completedSections={completedSections}
                                onSectionClick={onSectionChange}
                                validationErrors={validationErrors}
                            />
                        </div>

                        {/* Mobile Section Selector */}
                        <div className="lg:hidden">
                            <button
                                onClick={() => setShowMobileMenu(!showMobileMenu)}
                                className="w-full bg-white rounded-2xl shadow-lg p-4 flex items-center justify-between border border-gray-200"
                            >
                                <div className="flex items-center gap-3">
                                    {sections.find(s => s.id === currentSection)?.icon &&
                                        React.createElement(sections.find(s => s.id === currentSection).icon, {
                                            className: 'w-5 h-5 text-blue-600'
                                        })
                                    }
                                    <span className="font-semibold text-gray-900">
                                        {sections.find(s => s.id === currentSection)?.name || 'Select Section'}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-500">
                                    {completedCount}/{totalCount}
                                </div>
                            </button>

                            {showMobileMenu && (
                                <div className="mt-2 bg-white rounded-2xl shadow-lg border border-gray-200 max-h-96 overflow-y-auto">
                                    <ProgressSidebar
                                        sections={sections}
                                        currentSection={currentSection}
                                        completedSections={completedSections}
                                        onSectionClick={(sectionId) => {
                                            onSectionChange(sectionId);
                                            setShowMobileMenu(false);
                                        }}
                                        validationErrors={validationErrors}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Form Area */}
                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                            {/* Section Header */}
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-4">
                                        {sections.find(s => s.id === currentSection)?.icon &&
                                            React.createElement(sections.find(s => s.id === currentSection).icon, {
                                                className: 'w-8 h-8 text-blue-600 flex-shrink-0'
                                            })
                                        }
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900">
                                                {sections.find(s => s.id === currentSection)?.name || 'Section'}
                                                {sections.find(s => s.id === currentSection)?.required && (
                                                    <span className="text-red-500 ml-2">*</span>
                                                )}
                                            </h2>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {sections.find(s => s.id === currentSection)?.description || ''}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Form Content - This is your existing form */}
                            <div className="p-6">
                                {children}
                            </div>

                            {/* Section Navigation Buttons */}
                            <div className="border-t border-gray-200 bg-gray-50 p-6 flex items-center justify-between">
                                <button
                                    onClick={() => {
                                        const currentIndex = sections.findIndex(s => s.id === currentSection);
                                        if (currentIndex > 0) {
                                            onSectionChange(sections[currentIndex - 1].id);
                                        }
                                    }}
                                    disabled={sections.findIndex(s => s.id === currentSection) === 0}
                                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>

                                <div className="text-sm text-gray-600 hidden sm:block">
                                    Section {sections.findIndex(s => s.id === currentSection) + 1} of {sections.length}
                                </div>

                                <button
                                    onClick={() => {
                                        const currentIndex = sections.findIndex(s => s.id === currentSection);
                                        if (currentIndex < sections.length - 1) {
                                            onSectionChange(sections[currentIndex + 1].id);
                                        }
                                    }}
                                    disabled={sections.findIndex(s => s.id === currentSection) === sections.length - 1}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next Section
                                </button>
                            </div>
                        </div>

                        {/* Quick Tips Below Form */}
                        <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-100">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> You can skip optional sections and come back to them later.
                                Your progress is automatically saved every 30 seconds.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnhancedTemplateBuilder;
